import React, { createContext, useContext, useMemo } from 'react';
import { isOdoEmail } from '../utils/messageAuthor';

interface AvatarProps {
    user: { name: string; email: string };
    imageURL?: string;
    className?: string;
}

export interface ChatConfig {
    /** Determines if the message author is "me" (right-aligned) */
    isCurrentUser: (email: string | undefined) => boolean;
    /** Render an avatar — each app provides its own Avatar component */
    renderAvatar: (props: AvatarProps) => React.ReactNode;
    /** How to handle file attachment clicks */
    filePreviewMode: 'preview' | 'newTab';
    /** Whether message editing is enabled */
    editingEnabled: boolean;
    /** Whether message deletion is enabled */
    deletingEnabled: boolean;
    /** Edit and delete also apply to messages written by other ODO staff (super admins). */
    manageOdoStaffMessages?: boolean;
    /** Whether to render HTML messages via HtmlContent */
    htmlRenderingEnabled: boolean;
    /** Style variant for visual differences between apps */
    styleVariant: 'admin' | 'doctor';
    /** API base URL for proxy endpoints (e.g. PDF proxy) */
    apiBaseUrl: string;
    /**
     * fileId → displayable URL, for images embedded in a v2 body document.
     *
     * Image nodes carry a fileId and never a src: Monday's protected_static
     * links died with the migration, and a URL taken from stored content is an
     * SSRF footgun. The feed ships a signed URL per file alongside the messages.
     */
    resolveFileUrl?: (fileId: string) => string | undefined;
    /** Provide auth token for authenticated requests (e.g. PDF proxy) */
    getAuthToken?: () => string | null;
    /** Prose placeholder for the chat input. Keybind hint is appended automatically. */
    inputPlaceholder?: string;
    /**
     * Whether to render sent/seen receipts on messages authored by "my" side. Off by
     * default — only external, patient-facing conversations track seen-state, so each
     * app opts in explicitly (e.g. files-viewer keeps it off for the ODO-internal thread).
     */
    receiptsEnabled?: boolean;
    /**
     * When the other side (practice vs. ODO) last opened/marked-seen this chat, as an ISO
     * timestamp. Messages authored by "my" side with `createdAt` <= this value render a
     * seen (blue) receipt instead of a sent (gray) one. `null`/`undefined` means the other
     * side has no seen-state row yet; a never-seen row still resolves to an epoch ("1970-01-01")
     * timestamp rather than `null` — either way, no real message's `createdAt` will be <= it.
     */
    otherSideLastSeenAt?: string | null;
}

const defaultConfig: ChatConfig = {
    isCurrentUser: (email) => isOdoEmail(email),
    renderAvatar: ({ user }) => (
        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-white">
            {(user.name?.[0] ?? '?').toUpperCase()}
        </div>
    ),
    filePreviewMode: 'preview',
    editingEnabled: true,
    deletingEnabled: false,
    manageOdoStaffMessages: false,
    htmlRenderingEnabled: true,
    styleVariant: 'admin',
    apiBaseUrl: '',
    resolveFileUrl: undefined,
    getAuthToken: undefined,
    receiptsEnabled: false,
    otherSideLastSeenAt: null
};

const ChatConfigContext = createContext<ChatConfig>(defaultConfig);

interface ChatConfigProviderProps {
    config: Partial<ChatConfig>;
    children: React.ReactNode;
}

export const ChatConfigProvider: React.FC<ChatConfigProviderProps> = ({ config, children }) => {
    const merged = useMemo(() => ({ ...defaultConfig, ...config }), [config]);
    return <ChatConfigContext.Provider value={merged}>{children}</ChatConfigContext.Provider>;
};

export const useChatConfig = (): ChatConfig => useContext(ChatConfigContext);
